import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {LoadingButton} from "@mui/lab";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Stack
} from "@mui/material";
import Typography from "@mui/material/Typography";
import {LocalizedText} from "crqlar-react-common/dist/models/common/localizedText.model";
import {Survey, SurveyQuestionType} from "crqlar-react-common/dist/models/survey/survey.model";
import {
    SurveyReadinessCheck,
    SurveyReadinessStatus
} from "crqlar-react-common/dist/models/survey/surveyPublishInfo.model";
import {
    OFFERED_SURVEY_MEDIA_KINDS,
    SurveyMediaKind,
    SurveyQuestion
} from "crqlar-react-common/dist/models/survey/surveyQuestion.model";
import {SurveyService} from "crqlar-react-common/dist/services/survey/survey.service";
import {getNameForLanguage} from "crqlar-react-common/dist/utils/translations.util";
import {enqueueSnackbar} from "notistack";
import {useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {useQuery} from "react-query";
import {surveyMarkdownToPlainText} from "../shared/surveyMarkdownText.util";
import SurveyQuestionMarkers from "../shared/SurveyQuestionMarkers";
import {getSurveyQuestionTypeFacts} from "../shared/surveyQuestionTypeFacts";
import {getSurveyQuestionTypeIcon, getSurveyQuestionTypeLabelKey} from "../shared/surveyQuestionTypes.util";

interface Props {
    open: boolean
    survey: Survey
    onClose: () => void
    onPublished: () => void
}

const MEDIA_KIND_LABEL_KEYS: Partial<Record<SurveyMediaKind, string>> = {
    [SurveyMediaKind.IMAGE]: 'Images',
    [SurveyMediaKind.FILE]: 'Documents',
}

const checkStatusIcon = (status: SurveyReadinessStatus) => {
    if (status === SurveyReadinessStatus.COMPLETE) return <CheckCircleOutlineIcon color={'success'} fontSize={'small'}/>
    if (status === SurveyReadinessStatus.WARNING) return <InfoOutlinedIcon color={'warning'} fontSize={'small'}/>
    return <ErrorOutlineIcon color={'error'} fontSize={'small'}/>
}

const SurveyPublishDialog = ({open, survey, onClose, onPublished}: Props) => {

    const {t, i18n} = useTranslation()
    const [publishing, setPublishing] = useState(false)

    const surveyId = survey.id

    const publishInfoQuery = useQuery(
        ['surveyPublishInfo', surveyId, String(survey.updateDate ?? '')],
        () => SurveyService.publishInfo({id: surveyId!}),
        {enabled: !!surveyId, refetchOnMount: 'always', refetchOnWindowFocus: false}
    )
    const publishInfo = publishInfoQuery.data

    const liveVersion = publishInfo?.version ?? 0
    const nextVersion = liveVersion + 1
    const isReady = !!publishInfo?.isReady

    /*
        The staff member's own language wherever the survey offers it, exactly as the questions card
        on the survey page picks it. Leading with survey.languages[0] made the same German-and-
        English survey read German in this dialog and English on the page behind it.
    */
    const language = i18n.language
    const previewLanguage = survey.languages?.includes(language) ? language : survey.languages?.[0]

    const localizeTitle = useMemo(() => (text?: LocalizedText) => {
        if (!text) return undefined
        const raw = (previewLanguage && text[previewLanguage])
            || text[language]
            || text['en']
            || Object.values(text)[0]
        return surveyMarkdownToPlainText(raw)
    }, [language, previewLanguage])

    // Readiness checks name their question in values.question; a question with no text anywhere arrives blank,
    // and gets the same placeholder the accordion below uses.
    const checkValues = (check: SurveyReadinessCheck) => {
        const values = check.values
        if (typeof values?.question !== 'string' || values.question.trim()) return values
        return {...values, question: t('Untitled question')}
    }

    const publish = async () => {
        if (!surveyId) return

        setPublishing(true)
        try {
            await SurveyService.publishSurvey(surveyId)
            enqueueSnackbar(t('Survey published'), {
                variant: 'success',
                anchorOrigin: {vertical: 'bottom', horizontal: 'center'}
            })
            onPublished()
            onClose()
        } catch (e) {
            console.log(e)
            publishInfoQuery.refetch()
            enqueueSnackbar(t('The survey could not be published. Check the list of issues.'), {
                variant: 'error',
                anchorOrigin: {vertical: 'bottom', horizontal: 'center'}
            })
        } finally {
            setPublishing(false)
        }
    }

    const subtitle = liveVersion > 0
        ? t('Guests get v{{next}} for every new fill-out. Fill-outs in progress finish on v{{current}}.', {
            next: nextVersion,
            current: liveVersion
        })
        : t('This publishes v{{next}} - the first version guests can reach.', {next: nextVersion})

    const checkRow = (check: SurveyReadinessCheck, index: number) => (
        /* One id can appear once per question, so the id alone is not a unique key. */
        <Stack key={`${check.id}-${check.questionId ?? index}`} direction={'row'} alignItems={'flex-start'} gap={1.25}>
            <Box sx={{display: 'flex', mt: 0.25, flexShrink: 0}}>
                {checkStatusIcon(check.status)}
            </Box>
            <Box sx={{minWidth: 0, flex: 1}}>
                <Typography variant={'body2'} fontWeight={600}>
                    {t(check.label)}
                </Typography>
                <Typography variant={'body2'} color={'text.secondary'}>
                    {t(check.description, checkValues(check))}
                </Typography>
            </Box>
        </Stack>
    )

    const readinessSection = (() => {
        if (publishInfoQuery.isLoading) {
            return (
                <Stack alignItems={'center'} sx={{py: 3}}>
                    <CircularProgress size={24}/>
                </Stack>
            )
        }

        if (publishInfoQuery.isError || !publishInfo) {
            return (
                <Alert severity={'error'}>
                    {t('The publish state could not be loaded.')}
                </Alert>
            )
        }

        if (isReady) {
            // Warnings never block publishing, but they name what guests will notice, so they stay in view.
            const warnings = (publishInfo.checks ?? []).filter(check => check.status === SurveyReadinessStatus.WARNING)
            const counts = {count: publishInfo.questionCount, requiredCount: publishInfo.requiredCount}

            return (
                <Stack gap={1.5}>
                    <Alert severity={'success'} sx={{width: '100%'}}>
                        {warnings.length
                            ? t('Ready to publish - {{count}} questions, {{requiredCount}} required', counts)
                            : t('Ready to publish - {{count}} questions, {{requiredCount}} required, all checks complete', counts)}
                    </Alert>
                    {warnings.map(checkRow)}
                </Stack>
            )
        }

        return (
            <Stack gap={1}>
                {(publishInfo.checks ?? []).map(checkRow)}
            </Stack>
        )
    })()

    const metaRow = (label: string, value: string) => (
        <Stack key={label} gap={0.25} sx={{minWidth: 140}}>
            <Typography variant={'caption'} color={'text.secondary'}>{label}</Typography>
            <Typography variant={'body2'} sx={{wordBreak: 'break-word'}}>{value}</Typography>
        </Stack>
    )

    const questionFacts = (question: SurveyQuestion) => {
        const facts: JSX.Element[] = []

        const options = question.options ?? []
        if (options.length > 0) {
            const labels = options.map((option, index) => (
                localizeTitle(option.label)?.trim() || t('Option {{n}}', {n: index + 1})
            ))
            if (question.allowOther) labels.push(t('Other'))
            facts.push(metaRow(t('Options'), labels.join(', ')))
        }

        const scale = getSurveyQuestionTypeFacts(question.type)?.scale
        if (scale) {
            // The key spells out the 1 every scale starts at.
            facts.push(metaRow(t('Rating scale'), t('1 to {{highest}}', {highest: scale.max})))

            const low = localizeTitle(question.lowLabel)?.trim()
            const high = localizeTitle(question.highLabel)?.trim()
            if (low || high) {
                facts.push(metaRow(t('Endpoints'), [low || '—', high || '—'].join(' - ')))
            }
        }

        if (question.type === SurveyQuestionType.UPLOAD_MEDIA) {
            const kinds = (question.allowMediaKinds ?? [])
                .filter(kind => OFFERED_SURVEY_MEDIA_KINDS.includes(kind))
                .map(kind => t(MEDIA_KIND_LABEL_KEYS[kind] ?? kind))
            facts.push(metaRow(t('Accepted files'), kinds.length ? kinds.join(', ') : t('Any')))
        }

        if (question.type === SurveyQuestionType.LEGAL) {
            facts.push(metaRow(t('Signature'), question.requireSignature ? t('Required') : t('Not required')))
        }

        const titleLanguages = Object.entries(question.title ?? {})
            .filter(([, value]) => !!value?.trim())
            .map(([code]) => getNameForLanguage(code, true) || code)
        facts.push(metaRow(
            t('Languages'),
            titleLanguages.length ? titleLanguages.join(', ') : t('No text yet')
        ))

        const ruleCount = question.visibilityRules?.length ?? 0
        facts.push(metaRow(
            t('Visibility'),
            ruleCount === 0 ? t('Always') : t('{{count}} rules', {count: ruleCount})
        ))

        return facts
    }

    const questionAccordion = (question: SurveyQuestion, index: number) => {
        const title = localizeTitle(question.title)?.trim() || t('Untitled question')
        const optionCount = (question.options?.length ?? 0) + (question.allowOther ? 1 : 0)

        const meta = [
            t(getSurveyQuestionTypeLabelKey(question.type)),
            optionCount > 0 ? t('{{count}} options', {count: optionCount}) : undefined
        ].filter(Boolean).join(' · ')

        return (
            <Accordion key={question.id ?? `${index}-${question.type ?? 'unknown'}`}>
                <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                    <Stack direction={'row'} alignItems={'center'} gap={1} sx={{minWidth: 0, flex: 1}}>
                        {getSurveyQuestionTypeIcon(question.type)}
                        <Box sx={{minWidth: 0}}>
                            {/* Markers ride inside the wrapping title, where a squeezed Box cannot push them out. */}
                            <Typography variant={'body2'}>
                                {index + 1}. {title}
                                <SurveyQuestionMarkers question={question}/>
                            </Typography>
                            <Typography variant={'caption'} color={'text.secondary'}>
                                {meta}
                            </Typography>
                        </Box>
                    </Stack>
                </AccordionSummary>

                <AccordionDetails>
                    <Stack gap={0.75}>
                        {questionFacts(question)}
                    </Stack>
                </AccordionDetails>
            </Accordion>
        )
    }

    // What guests will see: publishing leaves a switched-off question out, so this list does too.
    const questions = (survey.questions ?? []).filter(question => !question.switchedOff)

    return (
        <Dialog open={open} onClose={onClose} maxWidth={'sm'} fullWidth>
            <DialogTitle sx={{pb: 1}}>
                <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} gap={1}>
                    <Typography variant={'h6'} fontWeight={600} noWrap>
                        {t('Publish survey')}
                    </Typography>

                    <IconButton onClick={onClose} size={'small'} aria-label={t('Close')}>
                        <CloseIcon/>
                    </IconButton>
                </Stack>
            </DialogTitle>

            <DialogContent dividers>
                <Stack gap={2.5}>
                    <Typography variant={'body2'} color={'text.secondary'}>
                        {subtitle}
                    </Typography>

                    {readinessSection}

                    <Divider/>

                    <Stack gap={1}>
                        <Typography variant={'subtitle2'}>
                            {t('What guests will see')}
                        </Typography>

                        {questions.length === 0 ? (
                            <Typography variant={'body2'} color={'text.secondary'}>
                                {t('No questions yet - open the editor to build the form.')}
                            </Typography>
                        ) : (
                            <Box>
                                {questions.map(questionAccordion)}
                            </Box>
                        )}
                    </Stack>
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} disabled={publishing}>
                    {t('Cancel')}
                </Button>

                <LoadingButton
                    variant={'contained'}
                    onClick={publish}
                    loading={publishing}
                    disabled={!isReady}
                >
                    {t('Publish v{{n}}', {n: nextVersion})}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    )
}

export default SurveyPublishDialog
